import { z } from 'zod'

const seoFieldsSchema = z
  .object({
    title: z.string().min(5).max(70).optional(),
    description: z.string().min(10).max(320).optional(),
    image: z.string().url().optional(),
  })
  .optional()

export const longCaseStudyPageNestedSchema = z.object({
  slug: z.string().min(2),
  title: z.string().min(5),
  read_time: z.string().optional(),
  /** ISO date or datetime from YAML/JSON tooling. */
  published_at: z.string().optional(),
  updated_at: z.string().optional(),
})

export const longCaseStudyHeroSchema = z.object({
  client: z.string().optional(),
  industry: z.string().optional(),
  /** Outline badges under the title; `focus_area` is shown last with filled treatment. */
  tags: z.array(z.string()).optional(),
  focus_area: z.string().optional(),
  core_problem: z.string().optional(),
  human_effort: z.string().optional(),
  acceleration: z.string().optional(),
  ai_handoff_potential: z.string().optional(),
  replicability: z.string().optional(),
  tools: z.array(z.string()).optional(),
  primary_cta_label: z.string().optional(),
  primary_cta_url: z.string().optional(),
})

export const longCaseStudyTldrSchema = z.object({ body: z.string().optional() }).optional()
const longCaseStudyQuoteFieldsSchema = z.object({
  body: z.string().optional(),
  /** Legacy single-line byline; ignored when `name` / `role` are set. */
  attribution: z.string().optional(),
  name: z.string().optional(),
  role: z.string().optional(),
  company: z.string().optional(),
  photo_url: z.string().optional(),
})

export const longCaseStudyQuoteSchema = longCaseStudyQuoteFieldsSchema.optional()

/** Shape of `quote`, `impact_quote`, and `faq_quote` blocks (optional wrapper stripped). */
export type LongCaseStudyQuoteFields = z.infer<typeof longCaseStudyQuoteFieldsSchema>

export const longCaseStudySeoSummarySchema = z.object({ body: z.string().optional() }).optional()

export const longCaseStudyProblemSchema = z
  .object({
    heading: z.string().optional(),
    paragraphs: z.array(z.string()).optional(),
  })
  .optional()

export const longCaseStudyOldWorkflowSchema = z
  .object({
    heading: z.string().optional(),
    intro: z.string().optional(),
    steps: z.array(z.string()).optional(),
  })
  .optional()

export const longCaseStudyReframingSchema = z
  .object({
    heading: z.string().optional(),
    paragraphs: z.array(z.string()).optional(),
  })
  .optional()

export const longCaseStudySolutionSchema = z
  .object({
    heading: z.string().optional(),
    paragraphs: z.array(z.string()).optional(),
    execution_steps: z
      .array(
        z.object({
          title: z.string(),
          description: z.string(),
        }),
      )
      .optional(),
    example: z
      .object({
        label: z.string().optional(),
        description: z.string().optional(),
        cta_label: z.string(),
        url: z.string().url(),
      })
      .optional(),
  })
  .optional()

export const longCaseStudyWorkflowSchemaSchema = z
  .object({
    type: z.string().optional(),
    body: z.string().optional(),
  })
  .optional()

export const longCaseStudyBeforeAfterSchema = z
  .object({
    heading: z.string().optional(),
    rows: z
      .array(
        z.object({
          metric: z.string(),
          before: z.string(),
          after: z.string(),
        }),
      )
      .optional(),
  })
  .optional()

export const longCaseStudyImpactSchema = z
  .object({
    heading: z.string().optional(),
    paragraphs: z.array(z.string()).optional(),
  })
  .optional()

export const longCaseStudyTransferabilitySchema = z
  .object({
    heading: z.string().optional(),
    paragraphs: z.array(z.string()).optional(),
  })
  .optional()

export const longCaseStudyGiftSchema = z
  .object({
    heading: z.string().optional(),
    body: z.string().optional(),
    cta_label: z.string().optional(),
    cta_url: z.string().optional(),
  })
  .optional()

export const longCaseStudyFaqSchema = z
  .object({
    items: z
      .array(
        z.object({
          question: z.string(),
          answer: z.string(),
        }),
      )
      .optional(),
  })
  .optional()

export const longCaseStudyAuthorSchema = z
  .object({
    /** Section `<h2>`; does not appear in the Content Sheet. */
    heading: z.string().optional(),
    name: z.string().optional(),
    role: z.string().optional(),
    email: z.string().email().optional(),
    bio_paragraphs: z.array(z.string()).optional(),
    photo_url: z.string().optional(),
    collaborators_heading: z.string().optional(),
    collaborators_body: z.string().optional(),
  })
  .optional()

export const longCaseStudyCollaboratorsSchema = z
  .object({
    heading: z.string().optional(),
    body: z.string().optional(),
    people: z
      .array(
        z.object({
          name: z.string(),
          role: z.string().optional(),
          photo_url: z.string().optional(),
          link_url: z.string().optional(),
          link_label: z.string().optional(),
        }),
      )
      .optional(),
  })
  .optional()

export const longCaseStudyClosingSchema = z
  .object({
    heading: z.string().optional(),
    paragraphs: z.array(z.string()).optional(),
    primary_cta_label: z.string().optional(),
    primary_cta_url: z.string().optional(),
    secondary_cta_label: z.string().optional(),
    secondary_cta_url: z.string().optional(),
  })
  .optional()

export const longCaseStudyReadAlsoSchema = z
  .object({
    items: z
      .array(
        z.object({
          type: z.string().optional(),
          title: z.string(),
          description: z.string().optional(),
          url: z.string(),
        }),
      )
      .optional(),
  })
  .optional()

const treeNodeSchema: z.ZodType<{ name: string; children?: Array<{ name: string; children?: unknown[] }> }> = z.lazy(
  () =>
    z.object({
      name: z.string(),
      children: z.array(treeNodeSchema).optional(),
    }),
)

export const longCaseStudyCustomBlockSchema = z
  .object({
    type: z.literal('tree-chart'),
    title: z.string().optional(),
    caption: z.string().optional(),
    data: treeNodeSchema,
  })

export const longCaseStudyCustomBlocksSchema = z
  .object({
    items: z.array(longCaseStudyCustomBlockSchema).optional(),
  })
  .optional()

export const longCaseStudyContentSheetSchema = z
  .object({
    enabled: z.boolean().optional(),
    items: z
      .array(
        z.object({
          label: z.string(),
          anchor: z.string(),
          depth: z.number().int().min(0).max(2).optional(),
        }),
      )
      .optional(),
  })
  .optional()

export const longCaseStudyFrontmatterSchema = z.object({
  type: z.literal('case-study'),
  template: z.literal('long'),
  visibility: z.enum(['public', 'private']).default('public'),
  /** Sorting / content-index (same as short case studies). */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
  tags: z.array(z.string()).default([]),
  seo: seoFieldsSchema,
  page: longCaseStudyPageNestedSchema,
  hero: longCaseStudyHeroSchema.optional(),
  tldr: longCaseStudyTldrSchema,
  quote: longCaseStudyQuoteSchema,
  /** Optional client voice after the Impact section (results / validation). */
  impact_quote: longCaseStudyQuoteSchema,
  /** Optional client voice immediately before FAQ (objections / reassurance). */
  faq_quote: longCaseStudyQuoteSchema,
  seo_summary: longCaseStudySeoSummarySchema,
  problem: longCaseStudyProblemSchema,
  old_workflow: longCaseStudyOldWorkflowSchema,
  reframing: longCaseStudyReframingSchema,
  solution: longCaseStudySolutionSchema,
  workflow_schema: longCaseStudyWorkflowSchemaSchema,
  before_after: longCaseStudyBeforeAfterSchema,
  impact: longCaseStudyImpactSchema,
  transferability: longCaseStudyTransferabilitySchema,
  gift: longCaseStudyGiftSchema,
  faq: longCaseStudyFaqSchema,
  author: longCaseStudyAuthorSchema,
  collaborators: longCaseStudyCollaboratorsSchema,
  closing: longCaseStudyClosingSchema,
  read_also: longCaseStudyReadAlsoSchema,
  custom_blocks: longCaseStudyCustomBlocksSchema,
  content_sheet: longCaseStudyContentSheetSchema,
})

export type LongCaseStudyFrontmatter = z.infer<typeof longCaseStudyFrontmatterSchema>

export type CaseStudyLong = LongCaseStudyFrontmatter & {
  content: string
}

export const isLongCaseStudy = (c: { type: string; template?: string }): c is CaseStudyLong =>
  c.type === 'case-study' && c.template === 'long'
